import { Injectable } from '@angular/core';
import { StoreObject, TypePolicies, TypePolicy } from '@apollo/client';
import { mergeDeep } from '@apollo/client/utilities';

import { ApolloTypePolicyProvider } from '../apollo-type-policy.provider';

const mergePolicyNoKey: TypePolicy = {
	keyFields: false,
	merge: true
};

// function/concept copied from DNG code.
const deepMergePolicyNoKey: TypePolicy = {
	keyFields: false,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	merge: (existing, incoming): any => {
		return mergeDeep(existing, incoming);
	}
};

const mergePolicyNoKeyKeepTypename: TypePolicy = {
	keyFields: false,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	merge: (existing, incoming): any => {
		// eslint-disable-next-line @typescript-eslint/naming-convention
		const __typename = existing ? existing.__typename : incoming.__typename;
		// eslint-disable-next-line @typescript-eslint/naming-convention
		return { ...existing, ...incoming, __typename };
	}
};

const mergePolicyFixedTypename = (typename: string): TypePolicy => {
	return {
		merge: true,
		fields: {
			// eslint-disable-next-line @typescript-eslint/naming-convention
			__typename: {
				// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
				merge(_existing, _incoming) {
					return typename;
				}
			}
		}
	};
};

@Injectable({
	providedIn: 'root'
})
export class SysApiTypePolicyProvider implements ApolloTypePolicyProvider {
	// eslint-disable-next-line @typescript-eslint/explicit-member-accessibility
	typenameMap(): { [__typename: string]: (storeObject: Readonly<StoreObject>) => string } {
		return {
			// eslint-disable-next-line @typescript-eslint/naming-convention, dot-notation
			DeviceChange: (storeObject) => `Device:${storeObject['id']}`,
			// eslint-disable-next-line @typescript-eslint/naming-convention, dot-notation
			RoomChange: (storeObject) => `Room:${storeObject['id']}`,
			// eslint-disable-next-line @typescript-eslint/naming-convention, dot-notation
			AudioChannelChange: (storeObject) => `AudioChannel:${storeObject['id']}`,
			// eslint-disable-next-line @typescript-eslint/naming-convention, dot-notation
			DeviceStatusChange: (storeObject) => `Device:${storeObject['id']}`,
			// eslint-disable-next-line @typescript-eslint/naming-convention, dot-notation
			RfChannelChange: (storeObject) => `RfChannel:${storeObject['id']}`,
			// eslint-disable-next-line @typescript-eslint/naming-convention, dot-notation
			ExternalDisplayChange: (storeObject) => `ExternalDisplay:${storeObject['id']}`
		};
	}

	public typePolicies(): TypePolicies {
		return {
			// TODO - need a scheme to ensure these type names are correct
			// - Inject via APOLLO_NAMED_OPTIONS or APOLLO_TYPE_POLICY_PROVIDERS?
			// - In the mean time, be sure your names match the schema type names exactly.
			RoomChange: mergePolicyFixedTypename('Room'),

			// Device
			DeviceChange: mergePolicyFixedTypename('Device'),
			DeviceStatusChange: mergePolicyFixedTypename('Device'),

			ExternalDisplayChange: mergePolicyFixedTypename('ExternalDisplay'),
			ExternalDisplayConnectionChange: mergePolicyNoKeyKeepTypename,
			ExternalDisplayResolutionChange: mergePolicyNoKeyKeepTypename,

			// First level in device which is not normalized (no id), need deep merge
			DeviceFeatures: deepMergePolicyNoKey,
			DeviceDescription: deepMergePolicyNoKey,
			DeviceSupportedFeatures: mergePolicyNoKey,

			DeviceAllEqualizersBypassChange: mergePolicyNoKeyKeepTypename,
			DeviceAllIntellimixBypassChange: mergePolicyNoKeyKeepTypename,
			DeviceAudioChannelCountChange: mergePolicyNoKeyKeepTypename,
			DeviceAudioMuteChange: mergePolicyNoKeyKeepTypename,
			DeviceAudioMuteControlGroupChange: mergePolicyNoKeyKeepTypename,
			DeviceAudioNetworkChange: mergePolicyNoKeyKeepTypename,
			DeviceAudioNetworkInterfaceChange: mergePolicyNoKeyKeepTypename,
			DeviceAuthenticationChange: mergePolicyNoKeyKeepTypename,
			DeviceAutomixGainMeteringChange: mergePolicyNoKeyKeepTypename,
			DeviceAvailablePackagesChange: mergePolicyNoKeyKeepTypename,
			DeviceBatteryLevelChange: mergePolicyNoKeyKeepTypename,
			DeviceCallStatusChange: mergePolicyNoKeyKeepTypename,
			DeviceCloudGatewayChange: mergePolicyNoKeyKeepTypename,
			DeviceConferencingPlatformChange: mergePolicyNoKeyKeepTypename,
			DeviceControlNetworkChange: mergePolicyNoKeyKeepTypename,
			DeviceControlNetworkInterfaceChange: mergePolicyNoKeyKeepTypename,
			DeviceCoverageModeChange: mergePolicyNoKeyKeepTypename,
			DeviceCoveragePositionChange: mergePolicyNoKeyKeepTypename,
			DeviceDanteAudioEncryptionChange: mergePolicyNoKeyKeepTypename,
			DeviceDanteAudioNetworkAccessChange: mergePolicyNoKeyKeepTypename,
			DeviceDanteAudioNetworkChange: mergePolicyNoKeyKeepTypename,
			DeviceEqualizerContourChange: mergePolicyNoKeyKeepTypename,
			DeviceEthernetLightsChange: mergePolicyNoKeyKeepTypename,
			DeviceExtendedWarrantyChange: mergePolicyNoKeyKeepTypename,
			DeviceExternalDisplaysChange: mergePolicyNoKeyKeepTypename,
			DeviceHeightChange: mergePolicyNoKeyKeepTypename,
			DeviceHostedFirmwareChange: mergePolicyNoKeyKeepTypename,
			DeviceHostedUpdateProgressChange: mergePolicyNoKeyKeepTypename,
			DeviceIdentifyChange: mergePolicyNoKeyKeepTypename,
			DeviceIotRoomProxyChange: mergePolicyNoKeyKeepTypename,
			DeviceLicenseChange: mergePolicyNoKeyKeepTypename,
			DeviceLicenseChangeV2: mergePolicyNoKeyKeepTypename,
			DeviceLicenseChangeV3: mergePolicyNoKeyKeepTypename,
			DeviceLicenseChannelCountChange: mergePolicyNoKeyKeepTypename,
			DeviceLocalLicenseChange: mergePolicyNoKeyKeepTypename,
			DeviceMeteringModesChange: mergePolicyNoKeyKeepTypename,
			DeviceMicStatusChange: mergePolicyNoKeyKeepTypename,
			DeviceMicStatusV2Change: mergePolicyNoKeyKeepTypename,
			DeviceNameChange: mergePolicyNoKeyKeepTypename,
			DevicePairedDevicesChange: mergePolicyNoKeyKeepTypename,
			DeviceProxiedDevicesChange: mergePolicyNoKeyKeepTypename,
			DeviceRoomChange: mergePolicyNoKeyKeepTypename,
			DeviceRotationChange: mergePolicyNoKeyKeepTypename,
			DeviceRoutingPositionChange: mergePolicyNoKeyKeepTypename,
			DeviceScheduledJobsChange: mergePolicyNoKeyKeepTypename,
			DeviceServerLicenseChange: mergePolicyNoKeyKeepTypename,
			DeviceTagsChange: mergePolicyNoKeyKeepTypename,
			DeviceUpdateProgressChange: mergePolicyNoKeyKeepTypename,
			DeviceUptimeChange: mergePolicyNoKeyKeepTypename,
			DeviceUserPresetsChange: mergePolicyNoKeyKeepTypename,

			// AudioChannel
			AudioChannelChange: mergePolicyFixedTypename('AudioChannel'),
			AudioChannelDescription: mergePolicyNoKey,
			AudioChannelSupportedFeatures: mergePolicyNoKey,
			AudioChannelFeatures: mergePolicyNoKey,

			AudioChannelAcousticEchoCancellationChange: mergePolicyNoKeyKeepTypename,
			AudioChannelAdvancedAutomixerChange: mergePolicyNoKeyKeepTypename,
			AudioChannelAecReferenceChange: mergePolicyNoKeyKeepTypename,
			AudioChannelAnalogInputChange: mergePolicyNoKeyKeepTypename,
			AudioChannelAnalogOutputChange: mergePolicyNoKeyKeepTypename,
			AudioChannelAutoFocusChange: mergePolicyNoKeyKeepTypename,
			AudioChannelAutoLevelChange: mergePolicyNoKeyKeepTypename,
			AudioChannelAutomaticGainControlChange: mergePolicyNoKeyKeepTypename,
			AudioChannelAutomixerChange: mergePolicyNoKeyKeepTypename,
			AudioChannelCompressorChange: mergePolicyNoKeyKeepTypename,
			AudioChannelDelayChange: mergePolicyNoKeyKeepTypename,
			AudioChannelMonitoringChange: mergePolicyNoKeyKeepTypename,
			AudioChannelDirectOutputChange: mergePolicyNoKeyKeepTypename,
			AudioChannelDanteEncryptionStatusChange: mergePolicyNoKeyKeepTypename,
			AudioChannelFaderGroupChange: mergePolicyNoKeyKeepTypename,
			AudioChannelGainChange: mergePolicyNoKeyKeepTypename,
			AudioChannelGateChange: mergePolicyNoKeyKeepTypename,
			AudioChannelIntellimixChange: mergePolicyNoKeyKeepTypename,
			AudioChannelLimiterChange: mergePolicyNoKeyKeepTypename,
			AudioChannelLobeChange: mergePolicyNoKeyKeepTypename,
			AudioChannelLogicChange: mergePolicyNoKeyKeepTypename,
			AudioChannelMixBusChange: mergePolicyNoKeyKeepTypename,
			AudioChannelMuteChange: mergePolicyNoKeyKeepTypename,
			AudioChannelMuteGroupChange: mergePolicyNoKeyKeepTypename,
			AudioChannelNameChange: mergePolicyNoKeyKeepTypename,
			AudioChannelNoiseReductionChange: mergePolicyNoKeyKeepTypename,
			AudioChannelPcInputChange: mergePolicyNoKeyKeepTypename,
			AudioChannelPcOutputChange: mergePolicyNoKeyKeepTypename,
			AudioChannelDanteRouteReceiverChange: mergePolicyNoKeyKeepTypename,
			AudioChannelDanteRouteTransmitterChange: mergePolicyNoKeyKeepTypename,
			AudioChannelSoloChange: mergePolicyNoKeyKeepTypename,
			AudioChannelSoloMixChange: mergePolicyNoKeyKeepTypename,
			AudioChannelThresholdLimiterChange: mergePolicyNoKeyKeepTypename,
			AudioChannelTalkerHeightChange: mergePolicyNoKeyKeepTypename,
			AudioChannelGroundLiftChange: mergePolicyNoKeyKeepTypename,

			EqualizerFilterChange: mergePolicyNoKeyKeepTypename,

			/*
			 * RfChannel
			 */
			// First level in device which is not normalized (no id), should deep merge
			RfChannelDescription: deepMergePolicyNoKey,
			// First level in device which is not normalized (no id), should deep merge
			RfChannelFeatures: deepMergePolicyNoKey,

			RfChannelChange: mergePolicyFixedTypename('RfChannel'),
			RfChannelFeatureChange: mergePolicyNoKeyKeepTypename,
			RfChannelLinkedTransmitterChange: mergePolicyNoKeyKeepTypename,
			RfChannelInterferenceDetectionChange: mergePolicyNoKeyKeepTypename,
			RfChannelInterferenceDetectorChange: mergePolicyNoKeyKeepTypename,
			RfChannelTuningChange: mergePolicyNoKeyKeepTypename,
			RfChannelMuteChange: mergePolicyNoKeyKeepTypename,

			// Cloud gateway
			CloudGatewayDiscoveredDevice: mergePolicyNoKeyKeepTypename,
			CloudGatewayDeviceDiscoveryResult: mergePolicyNoKeyKeepTypename,

			Subscription: {
				fields: {
					discoveredDevices: {
						// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
						merge(_e, i) {
							return i;
						}
					}
				}
			},
			Query: {
				fields: {
					discoveredDevices: {
						// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
						merge(_e, i) {
							return i;
						}
					}
				}
			}
		};
	}
}
